// src/components/mdx/client/visualizations/VideoRetentionCurveChart.client.tsx

/**
 * Interactive video retention curve with thumbstop and ThruPlay benchmark markers.
 */

'use client'

import { useMemo } from 'react'

import { BlogChartShell } from '~/components/mdx/client/visualizations/BlogChartShell.client'
import { BlogChartPlotArea } from '~/components/mdx/client/visualizations/blogChartLayout'
import { ChartVisibilityBoundary } from '~/components/mdx/client/visualizations/ChartVisibilityBoundary.client'
import { RetentionCurveChart } from '~/components/mdx/client/visualizations/RetentionCurveChart.client'
import { generateVideoRetentionData } from '~/components/mdx/client/visualizations/marketingDataVizData'

interface VideoRetentionCurveChartContentProps {
  isActive: boolean
}

function VideoRetentionCurveChartContent({ isActive }: VideoRetentionCurveChartContentProps): JSX.Element {
  const data = useMemo(() => generateVideoRetentionData(), [])

  return (
    <BlogChartShell
      title="Video Retention Curve"
      description="Diagnose hook, pacing, and hold problems from second-by-second viewer decay."
      gradient="from-purple-400 to-cyan-400"
    >
      <div className="mb-4 grid gap-2 sm:grid-cols-3">
        <div className="rounded-xl border border-orange-400/20 bg-orange-400/10 p-3 text-sm text-orange-100">
          <span className="font-semibold">0–3s cliff</span> → hook problem
        </div>
        <div className="rounded-xl border border-rose-400/20 bg-rose-400/10 p-3 text-sm text-rose-100">
          <span className="font-semibold">14–16s cliff</span> → mid-video pacing issue
        </div>
        <div className="rounded-xl border border-cyan-400/20 bg-cyan-400/10 p-3 text-sm text-cyan-100">
          <span className="font-semibold">Gradual slope</span> → acceptable for long-form
        </div>
      </div>

      <BlogChartPlotArea className="h-[360px] sm:h-[420px]">
        <RetentionCurveChart data={data} isActive={isActive} />
      </BlogChartPlotArea>
    </BlogChartShell>
  )
}

export default function VideoRetentionCurveChart(): JSX.Element {
  return (
    <ChartVisibilityBoundary aria-label="Video retention curve with thumbstop and ThruPlay markers">
      {({ isIntersecting }) => (
        <VideoRetentionCurveChartContent isActive={isIntersecting} />
      )}
    </ChartVisibilityBoundary>
  )
}
